import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { Icon, statusTone, type IconName } from '@/components/icon';

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
  {
    variants: {
      variant: {
        neutral: 'bg-muted text-muted-foreground',
        primary: 'bg-accent text-accent-foreground',
        success: 'bg-success-subtle text-success',
        warning: 'bg-warning-subtle text-warning',
        danger: 'bg-danger-subtle text-danger',
        info: 'bg-info-subtle text-info',
      },
    },
    defaultVariants: { variant: 'neutral' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  icon?: IconName;
}

export function Badge({ className, variant, icon, children, ...props }: BadgeProps): JSX.Element {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {icon ? <Icon name={icon} size={12} aria-hidden /> : null}
      {children}
    </span>
  );
}

/**
 * Live/lifecycle status pill — resolves the right icon + semantic tone from
 * `statusTone()` so every status renders consistently across the app (and SP2's
 * screen inventory). `label` overrides the displayed text (else the raw status).
 */
export function StatusPill({
  status,
  label,
  showIcon = true,
  ...props
}: {
  status: string;
  label?: string;
  showIcon?: boolean;
} & Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>): JSX.Element {
  const { icon, tone } = statusTone(status);
  return (
    <Badge variant={tone} data-status={status} {...(showIcon ? { icon } : {})} {...props}>
      {label ?? status}
    </Badge>
  );
}
